import { useEffect, useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, Shield, Clock, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Modal that collects customer info and renders Stripe embedded checkout to lock in a $499 refundable deposit.
 * Props: vehicle {id, year, make, model, price}, open, onClose
 */
export default function ReserveModal({ vehicle, open, onClose }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('info'); // 'info' | 'pay' | 'done'
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('info');
      setClientSecret(null);
      setForm({ name: '', email: '', phone: '' });
    }
  }, [open]);

  const fetchClientSecret = useCallback(async () => {
    if (clientSecret) return clientSecret;
    const res = await axios.post(`${API}/reservations/create-checkout`, {
      vehicle_id: vehicle.id,
      customer_name: form.name,
      customer_email: form.email,
      customer_phone: form.phone,
      origin_url: window.location.origin,
    });
    setClientSecret(res.data.client_secret);
    return res.data.client_secret;
  }, [vehicle, form, clientSecret]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t('carvana.reserve.errFill', 'Merci de remplir votre nom et courriel.'));
      return;
    }
    setLoading(true);
    try {
      await fetchClientSecret();
      setStep('pay');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="reserve-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto shadow-2xl relative my-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 z-10"
          data-testid="reserve-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="mb-6 pr-8">
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">
              {t('carvana.reserve.title', 'Réserver ce véhicule')}
            </p>
            <h2 className="text-2xl font-black text-[#0c2340]">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h2>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-600">
              <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-emerald-600" />{t('carvana.reserve.refundable', 'Dépôt remboursable')}</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-[#3b82f6]" />{t('carvana.reserve.hold30', 'Bloqué 30 minutes')}</span>
              <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" />{t('carvana.reserve.deposit', 'Dépôt de 499$ CAD')}</span>
            </div>
          </div>

          {step === 'info' && (
            <form onSubmit={handleContinue} className="space-y-4" data-testid="reserve-info-form">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">{t('carvana.reserve.name', 'Nom complet')}</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#3b82f6] focus:outline-none" data-testid="reserve-input-name" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">{t('carvana.reserve.email', 'Courriel')}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#3b82f6] focus:outline-none" data-testid="reserve-input-email" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">{t('carvana.reserve.phone', 'Téléphone')}</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#3b82f6] focus:outline-none" data-testid="reserve-input-phone" />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0c2340] hover:bg-[#0c2340]/90 text-white font-bold py-4 rounded-xl transition-colors disabled:opacity-50"
                data-testid="reserve-continue-btn"
              >
                {loading ? t('carvana.reserve.loading', 'Chargement...') : t('carvana.reserve.continue', 'Continuer vers le paiement — 499$ CAD')}
              </button>
              <p className="text-xs text-slate-500 text-center">
                {t('carvana.reserve.disclaimer', 'Votre dépôt est 100% remboursable pendant les 7 premiers jours. Le véhicule sera bloqué pour vous pendant 30 minutes.')}
              </p>
            </form>
          )}

          {step === 'pay' && clientSecret && (
            <div id="checkout" className="min-h-[400px]" data-testid="reserve-embedded-checkout">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
